import * as React from 'react'

import { SortDirection, Table, Text } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'

import { observer } from 'mobx-react'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'

import { getColumnsColspan } from '../tableColSpans'
import HeaderCheckbox from './_HeaderCheckbox'
import IconHeader from './_IconHeader'

export type ColumnAttributes =
  | 'first_name'
  | 'estimated_hours'
  | 'worked_hours'
  | 'hour_balance'
  | 'alert_counts'

interface Props {
  selected: boolean
  selectable: boolean
  onSelectAll?: (() => void) | null | undefined
  onDeselectAll?: (() => void) | null | undefined
  onSort: (attribute: string, direction: SortDirection) => void
  sortAttribute: string
  sortDirection: SortDirection
  hasOvertimeTimeOff: boolean
  showHourDistributionColumn: boolean
}

type Column = {
  name: string
  attribute: ColumnAttributes
  colSpan: number
  showColumn: boolean
  title: string | null
  description: string | null
}

const getTooltipDescription = (attribute: ColumnAttributes) => {
  switch (attribute) {
    case 'estimated_hours':
      return i18n.t('time_tracking.table.headers.estimated_tooltip_description')
    case 'worked_hours':
      return i18n.t('time_tracking.table.headers.worked_tooltip_description')
    default:
      return undefined
  }
}

const getTooltipInfo = (column: Column) => {
  const description = getTooltipDescription(column.attribute)

  if (!!description) {
    return (
      <IconHeader title={column.title} description={<Text.Normal>{description}</Text.Normal>} />
    )
  }
}

const Header = ({
  selected,
  selectable,
  onSelectAll,
  onDeselectAll,
  onSort,
  sortAttribute,
  sortDirection,
  hasOvertimeTimeOff,
  showHourDistributionColumn,
}: Props) => {
  const { featureAvailability: hasGeoLocation } = useAttendanceFeatures(
    Features.time_tracking_geofencing_alerts
  )
  const { featureAvailability: showRealTimeAttendance } = useAttendanceFeatures(
    Features.time_tracking_real_time_attendance
  )
  const { featureAvailability: hasTimeTrackingInconsistenciesEnabled } = useAttendanceFeatures(
    Features.time_tracking_time_alerts
  )

  const COLSPANS = getColumnsColspan()
  const overtimeBalanceDesc = i18n.t(
    'time_tracking.table.headers.hour_balance_hour_pool_tooltip_description'
  )
  const balanceOnlyDesc = i18n.t('time_tracking.table.headers.hour_balance_tooltip_description')
  const balanceDesc = hasOvertimeTimeOff ? overtimeBalanceDesc : balanceOnlyDesc

  const columns: Column[] = [
    {
      name: i18n.t('common.employee'),
      attribute: 'first_name',
      colSpan: COLSPANS.EMPLOYEE_COLUMN,
      showColumn: true,
      title: null,
      description: null,
    },
    {
      name: showRealTimeAttendance
        ? i18n.t('time_tracking.table.headers.planned_hours')
        : i18n.t('time_tracking.table.headers.estimated_hours'),
      attribute: 'estimated_hours',
      colSpan: COLSPANS.ESTIMATED_HOURS_COLUMN,
      showColumn: true,
      title: showRealTimeAttendance
        ? i18n.t('time_tracking.table.headers.planned_hours_tooltip_title')
        : i18n.t('time_tracking.table.headers.estimated_hours_tooltip_title'),
      description: i18n.t('time_tracking.table.headers.estimated_hours_tooltip_description'),
    },
    {
      name: i18n.t('time_tracking.table.headers.worked_hours'),
      attribute: 'worked_hours',
      colSpan: COLSPANS.WORKED_HOURS_COLUMN,
      showColumn: true,
      title: i18n.t('time_tracking.table.headers.worked_hours_title'),
      description: i18n.t('time_tracking.table.headers.worked_hours_description'),
    },
    {
      name: i18n.t('time_tracking.table.headers.hour_balance'),
      attribute: 'hour_balance',
      colSpan: COLSPANS.HOUR_BALANCE_COLUMN,
      showColumn: !hasGeoLocation,
      title: i18n.t('time_tracking.table.headers.hour_balance_tooltip_title'),
      description: balanceDesc,
    },
  ]

  selected && selectable && track(trackingEvents.timeTracking.bulkEmployeeSelect)

  return (
    <Table.Head>
      <Table.Row>
        {selectable && (
          <HeaderCheckbox
            onSelectAll={onSelectAll}
            onDeselectAll={onDeselectAll}
            selected={selected}
          />
        )}
        {columns
          .filter((c) => c.showColumn)
          .map((column) => (
            <Table.SortableHeadCell
              key={column.name}
              attribute={column.attribute}
              onSort={onSort}
              sortAttribute={sortAttribute}
              sortDirection={sortDirection}
              colSpan={column.colSpan}
              wide
              left={getTooltipInfo(column)}
            >
              {column.name}
            </Table.SortableHeadCell>
          ))}
        {showHourDistributionColumn && (
          <Table.HeadCell colSpan={COLSPANS.DISTRIBUTION_COLUMN} wide>
            {i18n.t('time_tracking.table.headers.distribution')}
          </Table.HeadCell>
        )}
        {hasGeoLocation && (
          <Table.SortableHeadCell
            key={i18n.t('time_tracking.table.headers.details')}
            attribute="hour_balance"
            onSort={onSort}
            sortAttribute={sortAttribute}
            sortDirection={sortDirection}
            colSpan={COLSPANS.HOUR_BALANCE_COLUMN}
            wide
            left={
              <IconHeader
                title={i18n.t('time_tracking.table.headers.details_tooltip_title')}
                description={
                  <>
                    <Text.Normal>{balanceDesc}</Text.Normal>
                    <Text.Normal>
                      {i18n.t('time_tracking.table.headers.overview_locations_tooltip_description')}
                    </Text.Normal>
                  </>
                }
              />
            }
          >
            {i18n.t('time_tracking.table.headers.details')}
          </Table.SortableHeadCell>
        )}
        {hasTimeTrackingInconsistenciesEnabled && (
          <Table.SortableHeadCell
            colSpan={COLSPANS.ALERTS_COLUMN}
            wide
            attribute="alert_counts"
            onSort={onSort}
            sortAttribute={sortAttribute}
            sortDirection={sortDirection}
            left={
              <IconHeader
                title={i18n.t('time_tracking.table.headers.alerts')}
                description={
                  <Text.Normal>
                    {i18n.t('time_tracking.table.headers.inconsistencies_alert_description')}
                  </Text.Normal>
                }
              />
            }
          >
            {i18n.t('time_tracking.table.headers.alerts')}
          </Table.SortableHeadCell>
        )}
        <Table.HeadCell width={100} />
      </Table.Row>
    </Table.Head>
  )
}

export default observer(Header)
